import { useState } from 'react';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { fetchJobs, retryAll, discardAll } from '../lib/api';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import type { JobSummary } from '../lib/api';
import { JobsTable } from '../components/JobsTable';
import { FailedJobDrawer } from '../components/FailedJobDrawer';
import {
  AuditRecordedNote,
  useAuditReasonConfirm,
} from '../components/AuditReasonConfirm';
import { opsErrorMessage } from '../lib/ops-api';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { TONE_TEXT } from '../components/Pill';
import { Icon } from '../components/Brand';
import { formatCount } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';

const ALL_QUEUES = [
  'invoice-processing',
  'export-processing',
  'webhook-processing',
  'data-retention',
] as const;

type QueueName = (typeof ALL_QUEUES)[number];

const PAGE_SIZE = 25;

export function FailedJobsPage() {
  const { t, tCount, locale } = useLocale();
  const queryClient = useQueryClient();
  // Bulk retry/discard write an OperatorAuditLog row with a typed reason (AC-G4c).
  const { ask, dialog } = useAuditReasonConfirm();
  const [selectedQueue, setSelectedQueue] = useState<QueueName | 'all'>('all');
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<
    (JobSummary & { queueName: string }) | null
  >(null);
  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [debouncedSearch, flushSearch] = useDebouncedValue(search);
  const [debouncedShopFilter, flushShopFilter] = useDebouncedValue(shopFilter);

  const applyFilters = () => {
    flushSearch();
    flushShopFilter();
  };

  // Fetch failed jobs for all queues in parallel using useQueries (avoids hooks-in-loop)
  const queueQueries = useQueries({
    queries: ALL_QUEUES.map((q) => ({
      queryKey: [
        'jobs',
        q,
        'failed',
        1,
        200,
        debouncedSearch,
        debouncedShopFilter,
      ] as const,
      queryFn: () =>
        fetchJobs(q, 'failed', 1, 200, {
          shopId: debouncedShopFilter.trim() || undefined,
          search: debouncedSearch.trim() || undefined,
        }),
      refetchInterval: 10_000,
    })),
  });

  const retryAllMutation = useMutation({
    mutationFn: (input: { queue: string; reason: string }) =>
      retryAll(input.queue, input.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  const discardAllMutation = useMutation({
    mutationFn: (input: { queue: string; reason: string }) =>
      discardAll(input.queue, input.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  // Merge all failed jobs across queues
  const allFailed: (JobSummary & { queueName: string })[] = [];
  ALL_QUEUES.forEach((q, i) => {
    const data = queueQueries[i]?.data;
    if (data) {
      data.jobs.forEach((job) => allFailed.push({ ...job, queueName: q }));
    }
  });

  // Filter by selected queue (search/shopId applied server-side)
  const filtered = allFailed.filter(
    (job) => selectedQueue === 'all' || job.queueName === selectedQueue,
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const isLoading = queueQueries.some((q) => q.isLoading);

  const countPerQueue = Object.fromEntries(
    ALL_QUEUES.map((q, i) => [q, queueQueries[i]?.data?.total ?? 0]),
  );
  const totalFailed = allFailed.length;

  const bulkActions =
    selectedQueue !== 'all' && (countPerQueue[selectedQueue] ?? 0) > 0 ? (
      <>
        <button
          onClick={() =>
            ask({
              title: t('reasonPrompt.queue.retryAll.title'),
              confirmLabel: t('reasonPrompt.queue.retryAll.confirm'),
              body: t('reasonPrompt.queue.retryAll.body', {
                queue: selectedQueue,
                count: countPerQueue[selectedQueue] ?? 0,
              }),
              onConfirm: (reason) =>
                retryAllMutation.mutate({ queue: selectedQueue, reason }),
            })
          }
          disabled={retryAllMutation.isPending || discardAllMutation.isPending}
          className="btn-secondary btn-sm gap-2"
        >
          <Icon name="refresh" className="h-4 w-4" />
          {retryAllMutation.isPending
            ? t('jobs.retrying')
            : t('jobs.retryAll', { count: countPerQueue[selectedQueue] ?? 0 })}
        </button>
        <button
          onClick={() =>
            ask({
              title: t('reasonPrompt.queue.discardAll.title'),
              confirmLabel: t('reasonPrompt.queue.discardAll.confirm'),
              body: t('reasonPrompt.queue.discardAll.body', {
                queue: selectedQueue,
                count: countPerQueue[selectedQueue] ?? 0,
              }),
              onConfirm: (reason) =>
                discardAllMutation.mutate({ queue: selectedQueue, reason }),
            })
          }
          disabled={retryAllMutation.isPending || discardAllMutation.isPending}
          className="btn-danger btn-sm gap-2"
        >
          <Icon name="trash" className="h-4 w-4" />
          {discardAllMutation.isPending
            ? t('jobs.discarding')
            : t('jobs.discardAll')}
        </button>
      </>
    ) : undefined;

  return (
    <>
      <PageHead
        eyebrow={t('common.operations')}
        title={t('failedJobs.title')}
        lead={tCount('failedJobs.lead', totalFailed)}
        actions={bulkActions}
      />

      <PageBody>
        {retryAllMutation.isSuccess && retryAllMutation.data && (
          <StatusBanner tone="success">
            {t('jobs.retried', {
              retried: retryAllMutation.data.retried,
              total: retryAllMutation.data.total,
            })}
            <AuditRecordedNote auditLogId={retryAllMutation.data.auditLogId} />
          </StatusBanner>
        )}
        {discardAllMutation.isSuccess && discardAllMutation.data && (
          <StatusBanner tone="success">
            {t('jobs.discarded', { count: discardAllMutation.data.discarded })}
            <AuditRecordedNote
              auditLogId={discardAllMutation.data.auditLogId}
            />
          </StatusBanner>
        )}
        {(retryAllMutation.isError || discardAllMutation.isError) && (
          <StatusBanner tone="error">
            {opsErrorMessage(
              retryAllMutation.error ?? discardAllMutation.error,
              t('reasonPrompt.failed'),
            )}
          </StatusBanner>
        )}

        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          {/* Queue filter tabs */}
          <div className="tabs overflow-x-auto">
            <button
              onClick={() => {
                setSelectedQueue('all');
                setPage(1);
              }}
              className={clsx(
                'tab',
                selectedQueue === 'all' ? 'tab-active' : 'tab-idle',
              )}
            >
              {t('failedJobs.all')}
              <span
                className={clsx(
                  'text-xs font-mono tabular-nums',
                  totalFailed > 0 ? TONE_TEXT.red : TONE_TEXT.gray,
                )}
              >
                {totalFailed}
              </span>
            </button>
            {ALL_QUEUES.map((q) => {
              const count = countPerQueue[q] ?? 0;
              // Short label for display
              const label = q.replace('-processing', '').replace('-', ' ');
              return (
                <button
                  key={q}
                  onClick={() => {
                    setSelectedQueue(q);
                    setPage(1);
                  }}
                  className={clsx(
                    'tab',
                    selectedQueue === q ? 'tab-active' : 'tab-idle',
                  )}
                >
                  <span className="capitalize">{label}</span>
                  <span
                    className={clsx(
                      'text-xs font-mono tabular-nums',
                      count > 0 ? TONE_TEXT.red : TONE_TEXT.gray,
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Shop filter */}
          <div className="flex-1 min-w-40">
            <input
              type="text"
              placeholder={t('failedJobs.shopFilterPlaceholder')}
              aria-label={t('common.shopFilterLabel')}
              value={shopFilter}
              onChange={(event) => {
                setShopFilter(event.target.value);
                setPage(1);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyFilters();
                  setPage(1);
                }
              }}
              className="field-input"
            />
          </div>

          {/* Search */}
          <div className="flex-1 min-w-60">
            <input
              type="search"
              placeholder={t('failedJobs.searchPlaceholder')}
              aria-label={t('failedJobs.searchLabel')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyFilters();
                  setPage(1);
                }
              }}
              className="field-input"
            />
          </div>
        </div>

        {/* Jobs table */}
        <div className="surface p-4 sm:p-5 overflow-x-auto">
          {isLoading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg" />
              ))}
            </div>
          ) : (
            <JobsTable
              jobs={paged}
              onSelect={(j) =>
                setSelectedJob(j as JobSummary & { queueName: string })
              }
              showQueue={selectedQueue === 'all'}
            />
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {t('failedJobs.pagination', {
                page,
                pages: totalPages,
                count: formatCount(filtered.length, locale),
              })}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="btn-neutral btn-sm"
              >
                {t('common.prev')}
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-neutral btn-sm"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </PageBody>

      {dialog}

      {/* Job detail drawer */}
      {selectedJob && (
        <FailedJobDrawer
          queueName={selectedJob.queueName}
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </>
  );
}
